import React, { Suspense, useEffect, useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { FormProvider, useForm } from "react-hook-form";
import { useSearchParams } from "react-router";

import ButtonLoading from "~/atoms/buttons/ButtonLoading";
import Link from "~/atoms/Link";
import Alert from "~/atoms/notifications/Alert";
import SkeletonLoader from "~/atoms/SkeletonLoader";
import Input from "~/components/form/Input";
import useAuthFlowCodes from "~/hooks/useAuthFlowCodes";
import { setLocalStorageItem } from "~/lib/localStorage";
import { useSession } from "~/providers/SessionProvider";
import type { UserRole } from "~/types";

interface FormData {
  email: string;
  password: string;
}

interface FormProps {
  type: UserRole;
  forgotUrl?: string;
  createUrl?: string;
  forgotOnClick?: () => void;
  createOnClick?: () => void;
  full?: boolean;
  onSuccess?: () => void;
}

const Notifications: React.FC = () => {
  const [searchParams] = useSearchParams();

  const passwordReset = searchParams.get("passwordReset");

  return (
    <>
      {passwordReset && (
        <Alert type="success">
          Your password has been reset. Please sign in.
        </Alert>
      )}
    </>
  );
};

const Form: React.FC<FormProps> = ({
  type,
  forgotUrl,
  createUrl,
  forgotOnClick,
  createOnClick,
  full,
  onSuccess,
}) => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const { signIn } = useSession();
  const { isLoading: isLoadingPresetEmail, email: presetEmail } =
    useAuthFlowCodes();

  const methods: UseFormReturn<FormData> = useForm<FormData>();
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!isLoadingPresetEmail && presetEmail) {
      methods.setValue("email", presetEmail);
    }
  }, [isLoadingPresetEmail, presetEmail, methods]);

  const onSubmit = async (data: FormData) => {
    setGeneralError(null);
    setLoading(true);

    const signinRes = await signIn(data.email, data.password, type, token);

    if (signinRes?.success) {
      // Redirect is handled on parent layout.
      setLocalStorageItem("role", type);

      if (typeof onSuccess === "function") {
        await onSuccess();
      }
    } else {
      setGeneralError(signinRes?.message || "An error occurred.");
      setLoading(false);
    }
  };

  return (
    <>
      <h2 className="mb-4 text-2xl leading-9 font-bold tracking-tight text-gray-900 capitalize">
        {type} Log In
      </h2>
      <Suspense>
        <Notifications />
      </Suspense>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} className="space-y-6">
          {isLoadingPresetEmail && (
            <div>
              <SkeletonLoader width={43} height={24} />
              <SkeletonLoader width="100%" height={36} className="mt-2" />
            </div>
          )}

          {!isLoadingPresetEmail && (
            <Input
              id="email"
              name="email"
              label="Email"
              type="email"
              required="Email is required."
              disabled={!!presetEmail}
              inputProps={{
                autoComplete: "email",
              }}
            />
          )}

          <Input
            id="password"
            name="password"
            label="Password"
            type="password"
            required="Password is required."
            inputProps={{
              autoComplete: "current-password",
            }}
          />

          {generalError && <Alert type="danger">{generalError}</Alert>}

          <div className="flex items-center justify-between">
            <div className="text-sm leading-6">
              {forgotUrl && (
                <Link
                  href={forgotUrl}
                  className="font-semibold text-blue-600 hover:text-blue-500"
                >
                  Forgot password?
                </Link>
              )}
              {forgotOnClick && (
                <button
                  type="button"
                  onClick={forgotOnClick}
                  className="font-semibold text-blue-600 hover:text-blue-500"
                >
                  Forgot password?
                </button>
              )}
            </div>
          </div>

          <div>
            <ButtonLoading loading={loading} type="submit" fullWidth={full}>
              Login
            </ButtonLoading>
          </div>
        </form>
      </FormProvider>
      <p className="mt-5 text-sm text-gray-500">
        Don&apos;t have an account?{" "}
        {createUrl && (
          <Link
            href={createUrl}
            className="leading-6 font-semibold text-blue-600 hover:text-blue-500"
          >
            Create one here
          </Link>
        )}
        {createOnClick && (
          <button
            type="button"
            onClick={createOnClick}
            className="leading-6 font-semibold text-blue-600 hover:text-blue-500"
          >
            Create one here
          </button>
        )}
      </p>
    </>
  );
};

export default Form;
